// Inventory Trends drawer — insight generator.
//
// Pure function. Takes an item + its current/prior-period invoice lines + an
// optional shrinkflation alert, returns a single plain-language sentence
// explaining "why is this changing?" — the question the drawer is built to
// answer.
//
// Two-tier priority model:
//
//   Tier 1 — durable signals, fire regardless of window-data sufficiency:
//     pack_changed-via-alert (signal_type='pack_count_drop')
//     shrinkflation         (signal_type='per_lb_spike')
//     needs_review          (item-level data integrity flag)
//
//   Tier 2 — require ≥ 2 invoices in the combined [prior, current] window:
//     pack_changed-via-window  (no alert; recent pack diff observable in window)
//     price_change             (|Δ avg($/lb)| ≥ 5%)
//     volume_change            (|Δ volume| ≥ 15%)
//     vendor_changed           (top vendor differs between periods)
//     stable                   (everything held)
//
//   insufficient — fallback when neither tier fires.
//
// The Tier-1 / Tier-2 split fixes a regression: a salt item with an active
// pack_count_drop alert but only 1 invoice in the current window would
// otherwise hit "insufficient" — hiding the killer feature. Durable signals
// are stored precisely so they don't depend on the operator's chosen window.
//
// Priority within Tier 1 reflects signal mechanics: observable cause >
// statistical consequence > data integrity. Within Tier 2: window-pack >
// price > volume > vendor > stable.
//
// See DESIGN_SYSTEM.md §Inventory detail drawer.

import { describeAlert, formatDetectionAge } from './shrinkflation';

// fmtPack — operator-readable pack label. pack_count=1 collapses to just
// piece_weight ("50#" instead of "1×50#"); n>1 keeps the cross format.
function fmtPack(line) {
  if (!line || line.pack_count == null) return null;
  const pc = Number(line.pack_count);
  const pw = line.piece_weight ? String(line.piece_weight).trim() : null;
  if (pc === 1 && pw) return pw;
  if (pw) return `${pc}\u00D7${pw}`;
  return `${pc}`;
}

function modePack(lines) {
  const counts = {};
  for (const l of lines) {
    const p = fmtPack(l);
    if (p) counts[p] = (counts[p] || 0) + 1;
  }
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return sorted[0]?.[0] || null;
}

function fmtDateShort(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtDetectedAt(timestampString) {
  if (!timestampString) return '';
  // first_detected_at is a full ISO timestamp; extract date portion for fmtDateShort.
  const dateOnly = String(timestampString).split('T')[0].split(' ')[0];
  return fmtDateShort(dateOnly);
}

function avg(lines, field) {
  const vals = lines.map(l => Number(l[field])).filter(v => v > 0 && isFinite(v));
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}

function totalVolume(lines, useWeight) {
  return useWeight
    ? lines.reduce((s, l) => s + (Number(l.total_weight) || 0), 0)
    : lines.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
}

function orderCount(lines) {
  const ids = new Set(lines.map(l => l.invoice_id).filter(v => v != null));
  return ids.size || lines.length;
}

function topVendor(lines) {
  const counts = {};
  for (const l of lines) {
    if (l.vendor_name) counts[l.vendor_name] = (counts[l.vendor_name] || 0) + 1;
  }
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return sorted[0]?.[0] || null;
}

function fmtVendor(v) {
  return v ? String(v).replace(/\n/g, ' \u00B7 ') : '';
}

// findPackLabelByValue — locate a labeled pack string on an invoice line whose
// numeric pack_count or piece_weight matches the target value (within 0.01).
// Used to render alert-derived branch-1 copy with operator-readable labels
// ("from 50# to 45#") instead of raw numerics ("from 50 to 45") when the
// underlying invoice rows are available.
function findPackLabelByValue(lines, value) {
  const target = Number(value);
  if (!isFinite(target)) return null;
  const tol = 0.01;
  for (const l of lines) {
    if (l.pack_count != null && Math.abs(Number(l.pack_count) - target) < tol) {
      const label = fmtPack(l);
      if (label) return label;
    }
    if (l.piece_weight) {
      const numeric = parseFloat(String(l.piece_weight).replace(/[^\d.]/g, ''));
      if (isFinite(numeric) && Math.abs(numeric - target) < tol) {
        const label = fmtPack(l);
        if (label) return label;
      }
    }
  }
  return null;
}

// inferUnitSuffix — extract a number-attached unit suffix from a pack label.
// Used to symmetrize "from N to M{suffix}" copy when only one side could be
// labeled from in-scope invoice data (alert references values that may have
// existed only outside the current window).
//   "50#"   → "#"
//   "10kg"  → "kg"
//   "3 L"   → ""    (space-separated, ambiguous — leave alone)
//   "16.5# AVG" → ""  (not a clean suffix — AVG is descriptive, not unit)
function inferUnitSuffix(label) {
  if (!label) return '';
  const m = String(label).match(/^[\d.]+([^\s\d.][^\s]*)?$/);
  return m && m[1] ? m[1] : '';
}

/**
 * generateInsight — single-sentence operator-direct insight for the Inventory
 * drawer headline. Returns `{ kind, headline }`.
 *
 * kind ∈ {
 *   pack_changed | shrinkflation | needs_review | price_change |
 *   volume_change | vendor_changed | stable | insufficient
 * }
 *
 * Tone rule: name the user's action ("you ordered…", "your $/lb…"), not the
 * data's behavior ("volume up X%"). Branch 1 (pack_changed) and branch 2
 * (shrinkflation) are the exceptions — pack changes and shrinkflation are
 * vendor actions, framed accordingly.
 */
export function generateInsight({ item, currLines = [], priorLines = [], alert = null, useWeight = false }) {
  const allLines = [...priorLines, ...currLines];

  // ─── Tier 1 — durable signals ────────────────────────────────────────────
  // Alert-driven pack change: fire even when the change happened outside the
  // current window. Try to pull labeled pack strings from invoice data; fall
  // back to raw numerics from the alert.
  if (alert && alert.signal_type === 'pack_count_drop') {
    const fromLabel = findPackLabelByValue(allLines, alert.baseline_value);
    const toLabel = findPackLabelByValue(allLines, alert.current_value);
    // Symmetrize copy when only one side resolved a label — e.g. salt window
    // contains only the post-drop "45#" line; we want "from 50# to 45#",
    // not "from 50 to 45#".
    const sharedSuffix = inferUnitSuffix(toLabel) || inferUnitSuffix(fromLabel);
    const fromText = fromLabel || `${Number(alert.baseline_value)}${sharedSuffix}`;
    const toText = toLabel || `${Number(alert.current_value)}${sharedSuffix}`;
    const detectedDate = fmtDetectedAt(alert.first_detected_at);
    const detectedClause = detectedDate ? `, detected ${detectedDate}` : '';
    return {
      kind: 'pack_changed',
      headline: `Pack size dropped from ${fromText} to ${toText}${detectedClause} \u2014 that's why $/case shifted.`,
    };
  }

  if (alert) {
    return {
      kind: 'shrinkflation',
      headline: `Shrinkflation alert: ${describeAlert(alert)}. Detected ${formatDetectionAge(alert.first_detected_at)}.`,
    };
  }

  if (item?.needs_review) {
    return {
      kind: 'needs_review',
      headline: 'Data review flag on this item \u2014 pack/weight values look suspicious. Resolve in the Data Review queue before reading the numbers below.',
    };
  }

  // ─── Tier 2 — require enough window data to compute comparisons ──────────
  const totalLines = currLines.length + priorLines.length;
  if (totalLines < 2) {
    // Enhanced sub-message when we have prior data but no current — the user
    // stopped ordering this item rather than "not enough history".
    if (currLines.length === 0 && priorLines.length >= 1) {
      const lastLine = priorLines[priorLines.length - 1];
      return {
        kind: 'insufficient',
        headline: `You haven't ordered this in the current period. Last order was ${fmtDateShort(lastLine.invoice_date)}.`,
      };
    }
    return {
      kind: 'insufficient',
      headline: 'Not enough invoice history yet \u2014 need at least 2 orders to spot a trend.',
    };
  }

  const unitLabel = useWeight ? 'lbs' : 'cases';

  // Window-based pack change (no alert backing it). Compares latest current
  // invoice's pack against the mode of prior period (or earlier current
  // lines when prior is empty).
  const sortedCurr = currLines.slice().sort((a, b) => String(a.invoice_date).localeCompare(String(b.invoice_date)));
  const latestCurr = sortedCurr[sortedCurr.length - 1] || null;
  const latestCurrPack = fmtPack(latestCurr);
  const baselinePack = priorLines.length > 0
    ? modePack(priorLines)
    : modePack(sortedCurr.slice(0, -1));
  if (latestCurrPack && baselinePack && latestCurrPack !== baselinePack) {
    return {
      kind: 'pack_changed',
      headline: `Pack size changed from ${baselinePack} to ${latestCurrPack} on ${fmtDateShort(latestCurr.invoice_date)} \u2014 that's why $/case shifted.`,
    };
  }

  // Numeric aggregates for branches 4-7.
  const cPerLb = avg(currLines, 'price_per_lb');
  const pPerLb = avg(priorLines, 'price_per_lb');
  const cUnit = avg(currLines, 'unit_price');
  const pUnit = avg(priorLines, 'unit_price');
  const cVol = totalVolume(currLines, useWeight);
  const pVol = totalVolume(priorLines, useWeight);
  const cOrders = orderCount(currLines);
  const pOrders = orderCount(priorLines);
  const cVendor = topVendor(currLines);
  const pVendor = topVendor(priorLines);

  // price_change — |Δavg($/lb)| ≥ 5%, falls back to $/case.
  const hasPerLb = cPerLb != null && pPerLb != null && pPerLb > 0;
  const hasCase = cUnit != null && pUnit != null && pUnit > 0;
  if (hasPerLb) {
    const pct = ((cPerLb - pPerLb) / pPerLb) * 100;
    if (Math.abs(pct) >= 5) {
      const dir = pct < 0 ? 'dropped' : 'climbed';
      const tail = pct < 0 ? "you're paying less per pound on the same item" : 'your unit cost is creeping up';
      return {
        kind: 'price_change',
        headline: `Your $/lb ${dir} ${Math.abs(pct).toFixed(0)}% this period \u2014 ${tail}.`,
      };
    }
  } else if (hasCase) {
    const pct = ((cUnit - pUnit) / pUnit) * 100;
    if (Math.abs(pct) >= 5) {
      const dir = pct < 0 ? 'dropped' : 'climbed';
      const tail = pct < 0 ? "you're paying less per case" : 'case price is creeping up';
      return {
        kind: 'price_change',
        headline: `Your $/case ${dir} ${Math.abs(pct).toFixed(0)}% this period \u2014 ${tail}.`,
      };
    }
  }

  // volume_change — |Δvolume| ≥ 15%, only when prior > 0.
  if (pVol > 0) {
    const pct = ((cVol - pVol) / pVol) * 100;
    if (Math.abs(pct) >= 15) {
      const orderDelta = cOrders - pOrders;
      let clause;
      if (orderDelta === 0) {
        clause = 'order frequency is unchanged \u2014 each order grew or shrank';
      } else if (orderDelta > 0) {
        clause = `you ordered this ${Math.abs(orderDelta)} ${Math.abs(orderDelta) === 1 ? 'time' : 'times'} more than last period`;
      } else {
        clause = `you ordered this ${Math.abs(orderDelta)} ${Math.abs(orderDelta) === 1 ? 'time' : 'times'} fewer than last period`;
      }
      const verbPhrase = pct > 0
        ? `pulled in ${Math.round(cVol).toLocaleString()} ${unitLabel} this period (+${pct.toFixed(0)}%)`
        : `cut back to ${Math.round(cVol).toLocaleString()} ${unitLabel} this period (\u2212${Math.abs(pct).toFixed(0)}%)`;
      return {
        kind: 'volume_change',
        headline: `You ${verbPhrase} \u2014 ${clause}.`,
      };
    }
  }

  // vendor_changed — top vendor differs.
  if (cVendor && pVendor && cVendor !== pVendor) {
    return {
      kind: 'vendor_changed',
      headline: `You switched from ${fmtVendor(pVendor)} to ${fmtVendor(cVendor)} this period \u2014 pricing and pack may differ between vendors.`,
    };
  }

  // stable — special-case prior-empty (first comparison window for this item).
  if (pOrders === 0) {
    const priceClause = cPerLb != null
      ? ` at ${`$${cPerLb.toFixed(2)}`}/lb avg`
      : (cUnit != null ? ` at ${`$${cUnit.toFixed(0)}`}/case avg` : '');
    return {
      kind: 'stable',
      headline: `${cOrders} ${cOrders === 1 ? 'order' : 'orders'} this period, ${Math.round(cVol).toLocaleString()} ${unitLabel} total${priceClause}. No prior-period activity to compare against.`,
    };
  }

  let priceClause = 'pricing held steady';
  if (hasPerLb) {
    const pct = Math.abs(((cPerLb - pPerLb) / pPerLb) * 100);
    priceClause = pct < 1 ? '$/lb held flat' : `$/lb moved within ${pct.toFixed(0)}%`;
  } else if (hasCase) {
    const pct = Math.abs(((cUnit - pUnit) / pUnit) * 100);
    priceClause = pct < 1 ? '$/case held flat' : `$/case moved within ${pct.toFixed(0)}%`;
  }
  return {
    kind: 'stable',
    headline: `Steady period \u2014 ${cOrders} ${cOrders === 1 ? 'order' : 'orders'}, ${Math.round(cVol).toLocaleString()} ${unitLabel}, ${priceClause} vs prior.`,
  };
}
